import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Box,
  IconButton,
  TextField,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { Print as PrintIcon } from "@mui/icons-material";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import debounce from "lodash.debounce";
import PATAddButton from "../../components/Buttons/PATAddButton";
import PrintsSearch3 from "../../components/PrintSearch3";
import CSVLinkWrapper from "../../components/CSVLinkWrapper";
import PATClearButton from "../../components/Buttons/PATClearButton";
import FilterToolbar from "../../components/FilterToolbar";
import SearchPersonDialog from "../../components/SearchPersonDialog";
import { useDispatch } from "react-redux";
import { createApplicant } from "../../reducers/ApplicantsReducer";
import { createRecordFor } from "../../utils/enums";
import { notify } from "../../reducers/NotifierReducer";
import {
  PATH_APPLICANTS,
  PATH_APPLICANTS_EDIT,
} from "../../utils/routeConstants";
import CreateFromPersonButton from "../../components/Buttons/CreateFromPersonButton";
import { areAllPropsEmpty, renderCodeOption } from "../../utils/functions";
import { getAccessPermission } from "../../reducers/PermissionsReducer";
import { getService } from "../../reducers/service";
import { screenNames, serviceNames } from "../../utils/constants";
import FilterButtons from "../../components/Buttons/FilterButtons";
import SelectFilterDialog from "../../components/SelectFilterDialog";
import SaveFilterDialog from "../../components/SaveFilterDialog";
import moment from "moment";

const headers = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "middleName", label: "Middle Name" },
  { key: "dob", label: "DOB", format: "date" },
  { key: "yearApplied", label: "Year Applied" },
  { key: "phone", label: "Phone" },
  { key: "ssn", label: "SSN" },
  { key: "ethnicityInfo.description", label: "Ethnicity" },
  { key: "genderInfo.description", label: "Gender" },
  { key: "applicantStatusInfo.description", label: "Status" },
];

const columnsForPrint = headers.map((h) => ({
  field: h.key,
  headerName: h.label,
  editable: false,
  flex: 1,
  ...h,
}));

const ApplicantsToolbar = ({
  applicants,
  keyboard,
  onAddClick,
  onFilterChange,
  filters,
  statusOptions,
  yearAppliedOptions,
  applicantTestResultOptions,
  genderOptions,
  raceOptions,
  ethnicityOptions,
  contactInfoOptions,
  tagTypeOptions,
  suffixOptions,
  resetFilters,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const userAgency = useSelector((state) => state.user.userAgency);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const [printFlag, setPrintFlag] = useState(false);
  const [kbdKey, setKbdKey] = useState(null);
  const [localFilters, setLocalFilters] = useState(filters);
  const [openSearchPersonDialog, setOpenSearchPersonDialog] = useState(false);
  const [openSelectFilterDialog, setOpenSelectFilterDialog] = useState(false);
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);

  const isFilterEmpty = areAllPropsEmpty(localFilters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);
  const applicantPermission = getAccessPermission(
    "applicants",
    "Applicant Record",
    "any"
  );
  const canCreateApplicants = !!applicantPermission?.Create;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      onFilterChange(newFilters);
    }, 500),
    [onFilterChange]
  );

  const handleCreateFromPerson = async (selectedPerson) => {
    if (!selectedPerson) {
      dispatch(notify("No person selected", "warning"));
      return;
    }

    const service = await getService(serviceNames.searchPeople);
    const res = await service.get(selectedPerson.ptsPersonID);

    let address = "";
    let zip = "";
    let state = "";
    let city = "";
    let race = "";
    let gender = "";

    if (res) {
      try {
        address = res.FullAddressText || null;
        zip = res.PostalCode || null;
        state = res.State || null;
        city = res.City || null;
      } catch (e) {
        console.error("Invalid address format", e);
      }
    }

    if (selectedPerson.Race) {
      const item = dictionary.codeRace.find(
        (r) => r.Code === selectedPerson.Race
      );
      race = item?.Code || selectedPerson.Race;
    }

    if (selectedPerson.Sex) {
      const item = dictionary.codeSex.find(
        (r) => r.Code === selectedPerson.Sex
      );
      gender = item?.Code || selectedPerson.Sex;
    }

    const applicantData = {
      firstName: selectedPerson.FirstName?.trim() || null,
      middleName: selectedPerson.MiddleName?.trim() || null,
      lastName: selectedPerson.LastName?.trim() || null,
      email: selectedPerson?.Email || res?.Email || null,
      phone: res?.PhoneNumber || null,
      workPhoneNum: res?.WorkPhoneNumber || res?.BusinessPhoneNumber || null,
      suffix: selectedPerson.Suffix?.trim() || null,
      ssn:
        selectedPerson.SSN?.replaceAll("-","")?.trim().length > 9
          ? null
          : selectedPerson.SSN?.trim(),
      ethnicity: selectedPerson?.Ethnicity?.trim() || null,
      race: race.trim() || null,
      gender: gender.trim() || null,
      dob: selectedPerson?.BirthDate ? moment(selectedPerson.BirthDate).startOf("day").format("YYYY-MM-DD HH:mm:ss.SSS") : null,
      bloodType: selectedPerson.BloodType || null,
      notes: selectedPerson.Notes || null,
      maritalStatus: selectedPerson.MaritalStatus || null,
      address: address || null,
      zip: zip || null,
      city: city || null,
      state: state || null,
      ptsPersonId: selectedPerson.ptsPersonID || null,
      agencyId: userAgency || null,
    };
    try {
      dispatch(createApplicant(applicantData)).then((result) => {
        if (result?.id) {
          history.push(`${PATH_APPLICANTS_EDIT}${result.id}`);
        } else {
          history.push(`${PATH_APPLICANTS}`);
        }
      });
    } catch (error) {
      dispatch(notify("Failed to create employee", "error"));
    }
  };

  const handleFilterChange = useCallback(
    (field, value) => {
      const updatedFilters = { ...localFilters, [field]: value };
      setLocalFilters(updatedFilters);
      debouncedFilterChange(updatedFilters);
    },
    [debouncedFilterChange, localFilters]
  );

  useEffect(() => {
    const subscription = keyboard.subject.subscribe((key) => {
      const validKeys = ["F5", "Ctrl+KeyP", "Ctrl+KeyL"];
      if (validKeys.includes(key)) setKbdKey(key);
    });
    return () => subscription.unsubscribe();
  }, [keyboard]);

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const renderFilterFields = (fields) =>
    fields.map(
      ({ type, label, field, options, xs = 2, multiselect = true }) => (
        <Grid item xs={isSmall ? 12 : xs} key={field}>
          {type === "text" ? (
            <TextField
              label={label}
              value={localFilters[field] || ""}
              onChange={(e) => handleFilterChange(field, e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
            />
          ) : (
            <Autocomplete
              multiple={multiselect}
              options={options || []}
              value={
                multiselect
                  ? localFilters[field] || []
                  : localFilters[field] || null
              }
              onChange={(_, value) => handleFilterChange(field, value)}
              getOptionLabel={(option) => option?.label || ""}
              renderOption={renderCodeOption}
              isOptionEqualToValue={(o, v) => o?.value === v?.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  variant="outlined"
                  size="small"
                />
              )}
              fullWidth
              sx={
                multiselect
                  ? {
                      "& .MuiAutocomplete-inputRoot": {
                        flexWrap: "nowrap",
                        maxHeight: "40px",
                      },
                      "& .MuiAutocomplete-tag": {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiAutocomplete-endAdornment": {
                        alignSelf: "flex-start",
                      },
                    }
                  : {}
              }
            />
          )}
        </Grid>
      )
    );

  const renderOptions = () => {
    return (
      <>
        {isSmall ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={applicants}
                headers={headers}
                filename="applicants.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!applicants?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Grid item>
              <CSVLinkWrapper
                data={applicants}
                headers={headers}
                filename="applicants.csv"
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handlePrintClick}
                color="primary"
                disabled={!applicants?.length}
              >
                <PrintIcon />
              </IconButton>
            </Grid>
          </Box>
        )}
      </>
    );
  };

  const renderCreateFromPerson = (condition = false) => {
    return condition ? (
      <Grid item>
        <CreateFromPersonButton
          tooltipText="Create from person record"
          onClick={() => setOpenSearchPersonDialog(true)}
        />
      </Grid>
    ) : null;
  };

  const renderAddButton = () => {
    return (
      <>
        {isSmall ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Grid item>
              <PATAddButton tooltipText="Add Applicant" onClick={onAddClick} />
            </Grid>
            {renderCreateFromPerson(true)}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Grid item>
              <PATAddButton tooltipText="Add Applicant" onClick={onAddClick} />
            </Grid>
            {renderCreateFromPerson(true)}
          </Box>
        )}
      </>
    );
  };

  const renderClearButton = () => {
    return (
      <>
        <Grid item>
          <Grid item>
            <PATClearButton title="Clear Filters" onClick={resetFilters} />
          </Grid>
        </Grid>
      </>
    );
  };

  const filterFields1 = [
    {
      type: "autocomplete",
      label: "Application Year",
      field: "yearApplied",
      options: yearAppliedOptions,
      multiselect: false,
      xs: 1.7,
    },
    { type: "text", label: "SSN", field: "ssn", xs: 1.7 },
    { type: "text", label: "Last Name", field: "lastName", xs: 1.7 },
    { type: "text", label: "First Name", field: "firstName", xs: 1.7 },
    { type: "text", label: "Middle Name", field: "middleName", xs: 1.7 },
    {
      type: "autocomplete",
      label: "Status",
      field: "status",
      options: statusOptions,
      xs: 1.7,
    },
    {
      type: "autocomplete",
      label: "Tags",
      field: "tagTypes",
      options: tagTypeOptions,
      xs: 1.7,
    },
    {
      type: "autocomplete",
      label: "PAT",
      field: "pat",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "Written",
      field: "written",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "Polygraph",
      field: "polygraph",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "Background",
      field: "background",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "First Interview",
      field: "oralCommittee",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "2nd Interview",
      field: "oralDirector",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "Medical",
      field: "medical",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1.5,
    },
    {
      type: "autocomplete",
      label: "Psych",
      field: "psych",
      options: applicantTestResultOptions,
      multiselect: false,
      xs: 1,
    },
  ];

  return (
    <>
      <Typography sx={{ fontSize: 20 }}>Applicants</Typography>
      <FilterToolbar
        isSmall={isSmall}
        canCreate={canCreateApplicants}
        leftSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: 1,
              }}
            >
              {renderAddButton()}
            </Box>
          )
        }
        middleSection={
          <Box
            sx={{
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              borderRadius: "10px",
              padding: 2,
              maxHeight: { xs: "25vh", md: "40vh" },
              overflowY: "auto",
              height: "100%",
              marginLeft: canCreateApplicants ? "" : 1.5,
            }}
          >
            <Grid container spacing={1}>
              {renderFilterFields([...filterFields1])}
              {renderClearButton()}
              <FilterButtons
                onSelectFilterClick={() => setOpenSelectFilterDialog(true)}
                onSaveFilterClick={() => setOpenSaveFilterDialog(true)}
                isFilterEmpty={isFilterEmpty}
              />
            </Grid>
          </Box>
        }
        rightSection={
          !isSmall && (
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.primary.main}`,
                borderRadius: "10px",
                padding: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                height: "100%",
                justifyContent: "center",
                marginRight: 1,
              }}
            >
              {renderOptions()}
            </Box>
          )
        }
        combinedActions={
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {renderAddButton()}
            {renderOptions()}
          </Box>
        }
        leftGridProps={{ xs: 12, md: 1 }}
        middleGridProps={{ xs: 12, md: 10 }}
        rightGridProps={{ xs: 12, md: 1 }}
      />

      <PrintsSearch3
        title="Applicants"
        data={applicants}
        cols={columnsForPrint}
        kbdKey={kbdKey}
        print={printFlag}
      />
      <SearchPersonDialog
        open={openSearchPersonDialog}
        onClose={() => setOpenSearchPersonDialog(false)}
        onConfirm={handleCreateFromPerson}
        title="Create Applicant from Person"
        confirmMessage={(p) =>
          `Create "${p?.FirstName} ${p?.LastName}" as an applicant?`
        }
        createFor={createRecordFor.Applicant}
        raceOptions={raceOptions}
        genderOptions={genderOptions}
        ethnicityOptions={ethnicityOptions}
        contactInfoOptions={contactInfoOptions}
        suffixOptions={suffixOptions}
      />

      <SelectFilterDialog
        open={openSelectFilterDialog}
        onClose={() => setOpenSelectFilterDialog(false)}
        screenName={screenNames.applicants}
        onFilterChange={onFilterChange}
      />

      <SaveFilterDialog
        open={openSaveFilterDialog}
        onClose={() => setOpenSaveFilterDialog(false)}
        screenName={screenNames.applicants}
        filters={localFilters}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  keyboard: state.keyboard,
  ...ownProps,
});

export default connect(mapStateToProps)(ApplicantsToolbar);
